import {useEffect, useState, useCallback} from 'react';
import {ThemeContext} from './themeContext';

// 'system' — полноценное третье состояние, а не синоним светлой: выбрав его,
// пользователь продолжает следовать за ОС, чего булевым флагом не выразить.
const THEMES = ['light', 'dark', 'system'];
const STORAGE_KEY = 'theme';

const systemPrefersDark = () =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(v) ? v : 'system';
  } catch {
    // Приватный режим / заблокированное хранилище: следуем за ОС.
    return 'system';
  }
};

// ВАЖНО: держать в синхроне с инлайновым скриптом в public/index.html — он
// ставит тот же класс до первой отрисовки, иначе страница мигает чужой темой.
const THEME_COLOR = {light: '#4872d7', dark: '#1b243c'};

const applyTheme = (resolved) => {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.style.colorScheme = resolved;
  // Иначе хром мобильного браузера останется светло-синим над тёмной страницей.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[resolved]);
};

export const ThemeProvider = ({children}) => {
  const [theme, setThemeState] = useState(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Слушатель имеет смысл только при theme === 'system', но он дёшев, а
  // безусловный проще для понимания, чем условный.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try {
      // 'system' пишется явно, а не удалением ключа: иначе смена дефолта
      // задним числом молча переинтерпретирует уже сделанный выбор.
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ничего страшного: на эту сессию тема всё равно применилась.
    }
  }, []);

  const toggle = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setTheme]);

  return (
    <ThemeContext.Provider value={{theme, resolved, setTheme, toggle}}>
      {children}
    </ThemeContext.Provider>
  );
};
